import log from 'loglevel'
import { CrossStorageClient } from 'cross-storage'
import {
  LOCAL_STORAGE_KEY_PREFIX,
  LOCAL_STORAGE_VISIBLE_KEY,
} from '@/constants.js'
import ObjectUtil from '@/js/util/ObjectUtil'

// localStorageに旧URL引き継ぎ済みフラグを保存するキー
const LOCAL_STORAGE_LAYER_VISIBLE_INHERITED_KEY = `${LOCAL_STORAGE_KEY_PREFIX}_layerVisibleInherited`

/**
 * 旧ページのlocalStorageへのクライアント(非リアクティブ)
 * @type {CrossStorageClient}
 */
let client

export function useInheritLayerVisible() {
  /**
   * すでに引き継ぎされているか？
   * @type {Boolean}
   */
  const isAlreadyInherited =
    (
      localStorage.getItem(LOCAL_STORAGE_LAYER_VISIBLE_INHERITED_KEY) || ''
    ).toLowerCase() === 'true'

  /**
   * 初期化
   */
  const init = () => {
    if (client) return
    if (isAlreadyInherited) {
      log.info(
        'Connection to legacy page unnecessary. layer visibles are already inherited.'
      )
      return
    }

    const _client = new CrossStorageClient(process.env.VUE_APP_LEGACY_URL, {
      frameId: 'ifield-legecy-page-frame',
    })
    return _client
      .onConnect()
      .then(() => {
        log.info('connection to legacy page is established!')
        client = _client
      })
      .catch((error) => {
        log.warn('failed to connect legacy page', error)
        _client.close()
      })
  }

  /**
   * client を廃棄
   */
  const dispose = () => {
    if (client) {
      client.close()
      log.info('connection to legacy page is closed.')
      client = undefined
    }
  }

  /**
   * レイヤーの表示状態を旧URlから引き継ぐ
   * @param {string} companyCode 企業コード
   * @param {boolean} [disposeOnSuccess=true] 正常終了時にclientを破棄する場合はtrue
   */
  const inheritLayerVisible = async (companyCode, disposeOnSuccess = true) => {
    if (isAlreadyInherited) {
      log.info('layer visibles are already inherited.')
      dispose()
      return
    }

    if (!client) {
      await init()
      if (!client) return
    }

    // 旧環境の値
    const legacyKey = `${LOCAL_STORAGE_KEY_PREFIX}_${companyCode}_visible`
    let legacyValue
    try {
      legacyValue = await client.get(legacyKey)
    } catch (error) {
      log.warn('failed to get layer visible from legacy page.', error)
      return
    }
    log.debug('[DEBUG] legacyValue', legacyValue)

    // 現環境の値
    const currentKey = LOCAL_STORAGE_VISIBLE_KEY
    const currentValue = localStorage.getItem(currentKey) || '{}'
    log.debug('[DEBUG] currentValue', currentValue)

    // マージして現環境に設定
    let newValue
    try {
      // 前回失敗時などでログイン後に既に変更されている場合があるので current を優先する
      // 汚染ガード付きの深いマージ（localStorage 由来の入力のため）
      newValue = ObjectUtil.deepMerge(
        JSON.parse(legacyValue),
        JSON.parse(currentValue)
      )
    } catch (error) {
      log.warn('failed to merge layer visible. values seems incorrect.', error)
      return
    }
    localStorage.setItem(currentKey, JSON.stringify(newValue))
    localStorage.setItem(LOCAL_STORAGE_LAYER_VISIBLE_INHERITED_KEY, true)

    if (disposeOnSuccess) {
      dispose()
    }
  }

  return { init, inheritLayerVisible }
}
